import { useEffect, useState } from "react";
import SearchableDropdown from "../components/SearchableDropdown";
import CameraCapture from "@/components/CameraCapture";
import { usersApi, eventsApi, apiErrorMessage } from "@/lib/api";
import { formatDate, formatDateForInput, localToday } from "@/lib/data";

export interface TaskFormValues {
  title: string;
  /** Event id as a string; "" = not tied to an event */
  eventId: string;
  /** User id of the assignee as a string */
  assignedToId: string;
  dueDate: string;
  priority: string;
  status: string;
  notes: string;
  before_image?: File | null;
  after_image?: File | null;
}

export interface TaskFormInitial extends Partial<TaskFormValues> {
  /** Shown if the assignee is no longer an active staff member */
  assignedToName?: string;
  /** Shown if the event isn't in the loaded list */
  eventName?: string;
  eventDate?: string;
}

interface TaskFormProps {
  onSubmit: (data: TaskFormValues) => void;
  onCancel: () => void;
  initialData?: TaskFormInitial;
  isEdit?: boolean;
  saving?: boolean;
}

interface StaffOption { id: number; name: string; role?: string }
interface EventOption { id: number; name: string; start_date?: string | null; end_date?: string | null; status?: string }

const priorities = ["high", "medium", "low"];
const statuses = ["pending", "in_progress", "completed"];
const HIDDEN_EVENT_STATUSES = ["cancelled", "completed"];

// Kept between openings of the form so the pickers fill instantly; refreshed on every open.
const cache: { staff?: StaffOption[]; events?: EventOption[] } = {};

/** Event dates arrive as UTC timestamps (local midnight = 18:30 the day before), so convert to the local day */
const d10 = (s?: string | null) => formatDateForInput(s);
const eventLabel = (e: { name: string; start_date?: string | null }) =>
  e.start_date ? `${e.name} · ${formatDate(e.start_date)}` : e.name;

export default function TaskForm({ onSubmit, onCancel, initialData, isEdit = false, saving = false }: TaskFormProps) {
  const [formData, setFormData] = useState<TaskFormValues>({
    title: initialData?.title || "",
    eventId: initialData?.eventId || "",
    assignedToId: initialData?.assignedToId || "",
    dueDate: initialData?.dueDate || "",
    priority: initialData?.priority || "medium",
    status: initialData?.status || "pending",
    notes: initialData?.notes || "",
    before_image: null,
    after_image: null,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof TaskFormValues, string>>>({});

  const [staff, setStaff] = useState<StaffOption[] | undefined>(cache.staff);
  const [events, setEvents] = useState<EventOption[] | undefined>(cache.events);
  const [staffError, setStaffError] = useState<string | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const loadStaff = async () => {
    setStaffError(null);
    try {
      const res = await usersApi.list({ status: "active" });
      const list: StaffOption[] = (res?.data?.data || res?.data || []).map((u: any) => ({ id: Number(u.id), name: u.name, role: u.role }));
      cache.staff = list;
      setStaff(list);
    } catch (error) {
      setStaffError(apiErrorMessage(error, "Couldn't load staff"));
    }
  };

  const loadEvents = async () => {
    setEventsError(null);
    try {
      const res = await eventsApi.list();
      const list: EventOption[] = (res?.data?.data || res?.data || []).map((e: any) => ({
        id: Number(e.id), name: e.name, start_date: e.start_date, end_date: e.end_date, status: e.status,
      }));
      cache.events = list;
      setEvents(list);
    } catch (error) {
      setEventsError(apiErrorMessage(error, "Couldn't load events"));
    }
  };

  useEffect(() => {
    loadStaff();
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A linked event that doesn't exist (deleted, mistyped link) can't be saved — drop it
  useEffect(() => {
    if (events && formData.eventId && !events.some((e) => String(e.id) === formData.eventId) && !initialData?.eventName) {
      setFormData((prev) => ({ ...prev, eventId: "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // Active staff; keep the current assignee listed even if they've since been deactivated
  const staffOptions = (() => {
    const opts = (staff || []).map((s) => ({ value: String(s.id), label: s.role ? `${s.name} · ${s.role}` : s.name }));
    if (formData.assignedToId && !opts.some((o) => o.value === formData.assignedToId) && initialData?.assignedToName) {
      opts.unshift({ value: formData.assignedToId, label: initialData.assignedToName });
    }
    return opts;
  })();

  // Open events, soonest first (upcoming, then ones whose date has passed); the selected one always stays listed
  const eventOptions = (() => {
    const today = localToday();
    const selected = formData.eventId;
    const shown = (events || []).filter((e) => !HIDDEN_EVENT_STATUSES.includes(e.status || "") || String(e.id) === selected);
    const endOf = (e: EventOption) => d10(e.end_date || e.start_date);
    const upcoming = shown.filter((e) => endOf(e) >= today).sort((a, b) => d10(a.start_date).localeCompare(d10(b.start_date)));
    const past = shown.filter((e) => endOf(e) < today).sort((a, b) => d10(b.start_date).localeCompare(d10(a.start_date)));
    const opts = [...upcoming, ...past].map((e) => ({ value: String(e.id), label: eventLabel(e) }));
    if (selected && !opts.some((o) => o.value === selected) && initialData?.eventName) {
      opts.unshift({ value: selected, label: eventLabel({ name: initialData.eventName, start_date: initialData.eventDate }) });
    }
    return [{ value: "", label: "No event (general task)" }, ...opts];
  })();

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TaskFormValues, string>> = {};
    if (!formData.title.trim()) newErrors.title = "Give the task a title";
    if (!formData.assignedToId) newErrors.assignedToId = "Choose who will do this";
    if (!formData.dueDate) newErrors.dueDate = "Pick a due date";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (validate()) onSubmit(formData);
  };

  const handleChange = (field: keyof TaskFormValues, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const inputClass = (field: keyof TaskFormValues) => `
    w-full min-h-10 px-3 py-2.5 rounded-xl border text-sm transition-colors
    ${errors[field]
      ? "border-rose-300 bg-rose-50 focus:border-rose-400 focus:ring-2 focus:ring-rose-100"
      : "border-stone-200 bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
    }
    outline-none
  `;

  const loadProblem = (message: string, retry: () => void) => (
    <p className="text-xs text-rose-600 mt-1">
      {message}.{" "}
      <button type="button" onClick={retry} className="font-semibold underline min-h-10 sm:min-h-0">Try again</button>
    </p>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Task Title */}
        <div className="sm:col-span-2">
          <label htmlFor="task-title" className="block text-xs font-medium text-stone-500 mb-1.5">Task title *</label>
          <input
            id="task-title"
            type="text"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder="e.g., Book floral decorator"
            className={inputClass("title")}
          />
          {errors.title && <p className="text-xs text-rose-500 mt-1">{errors.title}</p>}
        </div>

        {/* Event */}
        <div className="sm:col-span-2">
          <SearchableDropdown
            label="Event"
            options={eventOptions}
            value={formData.eventId}
            onChange={(value) => handleChange("eventId", value)}
            placeholder={events ? "Choose an event (optional)" : "Loading events…"}
          />
          {eventsError && loadProblem(eventsError, loadEvents)}
        </div>

        {/* Assigned To */}
        <div>
          <SearchableDropdown
            label="Assigned to *"
            options={staffOptions}
            value={formData.assignedToId}
            onChange={(value) => handleChange("assignedToId", value)}
            placeholder={staff ? "Choose a staff member" : "Loading staff…"}
            error={errors.assignedToId}
          />
          {staffError && loadProblem(staffError, loadStaff)}
        </div>

        {/* Due Date */}
        <div>
          <label htmlFor="task-due" className="block text-xs font-medium text-stone-500 mb-1.5">Due date *</label>
          <input
            id="task-due"
            type="date"
            value={formData.dueDate}
            onChange={(e) => handleChange("dueDate", e.target.value)}
            className={inputClass("dueDate")}
          />
          {errors.dueDate && <p className="text-xs text-rose-500 mt-1">{errors.dueDate}</p>}
        </div>

        {/* Priority */}
        <div>
          <label className="block text-xs font-medium text-stone-500 mb-1.5">Priority</label>
          <div className="flex gap-2" role="group" aria-label="Priority">
            {priorities.map((p) => (
              <button
                key={p}
                type="button"
                aria-pressed={formData.priority === p}
                onClick={() => handleChange("priority", p)}
                className={`flex-1 h-10 rounded-xl text-xs font-semibold transition-colors ${
                  formData.priority === p
                    ? p === "high" ? "bg-rose-100 text-rose-700 border border-rose-200"
                      : p === "medium" ? "bg-amber-100 text-amber-700 border border-amber-200"
                      : "bg-stone-100 text-stone-600 border border-stone-200"
                    : "bg-stone-50 text-stone-400 border border-stone-200 hover:bg-stone-100"
                }`}
              >
                {p === "high" ? "🔴" : p === "medium" ? "🟡" : "⚪"} {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label htmlFor="task-status" className="block text-xs font-medium text-stone-500 mb-1.5">Status</label>
          <select
            id="task-status"
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            className={inputClass("status")}
          >
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s === "in_progress" ? "In progress" : s === "completed" ? "Completed" : "Pending"}
              </option>
            ))}
          </select>
        </div>

        {/* Notes */}
        <div className="sm:col-span-2">
          <label htmlFor="task-notes" className="block text-xs font-medium text-stone-500 mb-1.5">Notes</label>
          <textarea
            id="task-notes"
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            placeholder="Additional details or instructions..."
            rows={3}
            className={`${inputClass("notes")} resize-none`}
          />
        </div>

        {/* Task Photos Section */}
        <div className="sm:col-span-2">
          <label className="block text-xs font-medium text-stone-500 mb-3">Task photos (camera)</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CameraCapture
              label="Before Photo"
              description="Capture photo before task"
              onCapture={(file) => setFormData((prev) => ({ ...prev, before_image: file }))}
            />
            <CameraCapture
              label="After Photo"
              description="Capture photo after completion"
              onCapture={(file) => setFormData((prev) => ({ ...prev, after_image: file }))}
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-stone-100">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl bg-stone-100 text-stone-600 font-semibold text-sm hover:bg-stone-200 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex-1 h-11 px-4 rounded-xl text-white font-semibold text-sm gold-gradient hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : isEdit ? "Save changes" : "Add task"}
        </button>
      </div>
    </form>
  );
}
